/**
 * Trade Company Page — manages company details, team members,
 * and resale certificate views.
 *
 * Loaded by sections/trade-company.liquid.
 * Reads initial view from URL hash (#team-members, #resale-certificate).
 * Stores company data in window.currentCompanyData for modal use.
 * Listens for trade:member-invited, trade:company-updated, and
 * trade:certificate-uploaded events to refresh the active view.
 */
(function () {
  document.addEventListener('DOMContentLoaded', function () {
    const page = document.getElementById('trade-company-page');
    if (!page) return;

    const certificateFileIconUrl = page.dataset.certificateIconUrl || '';
    const companyContentContainer = document.querySelector('.company-content');

    // Cache view elements and URL params up front
    const companyContentView = document.getElementById('company-content-view');
    const loadingState = document.getElementById('loading-state');
    const hiddenClass = 'trade-modal__section--hidden';
    let isCurrentUserAdmin = false;
    let currentTeamMembers = [];

    const renderTradeState = (options = {}) => window.renderTradeState({
      ...options,
      containerEl: companyContentContainer
    });
    const escapeHtml = window.escapeTradeHtml;

    // Read initial section from URL hash
    let section = ''; // Company Details view as Default
    if (window.location.hash) {
      const hash = window.location.hash.substring(1); // Remove the # character
      if (hash === 'team-members' || hash === 'resale-certificate') {
        section = hash;
      }
    }

    // Show loading state
    const showLoading = () => {
      if (companyContentView) companyContentView.classList.add(hiddenClass);
      if (loadingState) loadingState.classList.remove(hiddenClass);
    };

    // Hide loading state
    const hideLoading = () => {
      if (loadingState) loadingState.classList.add(hiddenClass);
    };

    // Encapsulate showing / hiding logic
    const toggleView = () => {
      // Update URL hash to match current section
      if (section) {
        window.location.hash = section;
      } else {
        // Remove hash if on company details view
        history.replaceState(null, null, window.location.pathname + window.location.search);
      }

      // Always show the main content view, content will change based on view parameter
      if (companyContentView) {
        companyContentView.classList.remove(hiddenClass);
      }
      hideLoading();

      // Ensure footer is always visible
      const footer = document.getElementById('company-footer');
      if (footer) {
        footer.classList.remove(hiddenClass);
      }

      // Update header based on section
      updateHeaderForView(section);

      // Render appropriate content based on section
      switch (section) {
        case 'team-members':
          renderTeamMembers();
          break;
        case 'resale-certificate':
          renderResaleCertificate();
          break;
        default:
          renderCompanyDetails();
          break;
      }
    };

    const updateHeaderForView = (currentSection) => {
      const headerTitle = document.querySelector('.trade-page-header h1');
      const companyActions = document.querySelector('.company-actions');
      const dropdownText = document.getElementById('company-dropdown-text');

      // H1 always remains "Company Details"
      if (headerTitle) {
        headerTitle.textContent = 'Company Details';
      }

      // Update mobile dropdown text
      if (dropdownText) {
        switch (currentSection) {
          case 'team-members':
            dropdownText.textContent = 'Team Members';
            break;
          case 'resale-certificate':
            dropdownText.textContent = 'Resale Certificate';
            break;
          default:
            dropdownText.textContent = 'Company Details';
            break;
        }
      }

      // Update active button state for desktop
      if (companyActions) {
        const buttons = companyActions.querySelectorAll('button');
        // Reset all buttons to secondary (outline) state
        buttons.forEach((btn) => {
          btn.classList.remove('Button--primary');
          btn.classList.add('Button--secondary');
        });

        // Make the selected button primary
        switch (currentSection) {
          case 'team-members':
            if (buttons[1]) { buttons[1].classList.remove('Button--secondary'); buttons[1].classList.add('Button--primary'); }
            break;
          case 'resale-certificate':
            if (buttons[2]) { buttons[2].classList.remove('Button--secondary'); buttons[2].classList.add('Button--primary'); }
            break;
          default:
            if (buttons[0]) { buttons[0].classList.remove('Button--secondary'); buttons[0].classList.add('Button--primary'); }
            break;
        }
      }
    };

    const getDetailValue = (value, fallback = 'Not set') => {
      if (value === null || value === undefined) return fallback;
      if (typeof value === 'string' && value.trim() === '') return fallback;
      return value;
    };

    const getMemberTypeLabel = (member = {}) => {
      if (member.isPending) return 'Invited';
      return member.isAdmin ? 'Account Administrator' : 'Team Member';
    };

    const getTeamMemberRowId = (member = {}) => {
      const fallback = [member.name, member.phone, getMemberTypeLabel(member)].filter(Boolean).join('|');
      const rawValue = member.email || member.id || fallback;
      return encodeURIComponent(String(rawValue || '').trim().toLowerCase());
    };

    const renderTeamMemberText = (value, fallback = 'Not set') => {
      return `<span class="trade-data-list__text">${escapeHtml(getDetailValue(value, fallback))}</span>`;
    };

    const createTeamMemberListConfig = (teamMembers = []) => {
      return {
        variant: 'team-members',
        rows: teamMembers,
        features: {
          showHeaders: true,
          clickableRows: true,
          stackedMobile: true
        },
        columns: [
          {
            key: 'avatar',
            label: '',
            width: 'minmax(0, 50px)',
            cellClassName: 'trade-data-list__cell--avatar',
            renderCell: (member) => `<span class="avatar">${escapeHtml(member.initials || '')}</span>`,
            hideMobileLabel: true
          },
          {
            key: 'name',
            label: 'Name',
            mobileLabel: 'Name',
            width: 'minmax(0, 300px)',
            cellClassName: 'trade-data-list__cell--team-member',
            renderCell: (member) => {
              const pendingBadge = member.isPending
                ? '<span class="member-badge--pending">Pending</span>'
                : '';
              return `${renderTeamMemberText(member.name)}${pendingBadge}`;
            }
          },
          {
            key: 'email',
            label: 'Email',
            mobileLabel: 'Email',
            width: 'minmax(0, 300px)',
            cellClassName: 'trade-data-list__cell--team-member',
            renderCell: (member) => renderTeamMemberText(member.email)
          },
          {
            key: 'phone',
            label: 'Phone',
            mobileLabel: 'Phone',
            width: 'minmax(0, 300px)',
            cellClassName: 'trade-data-list__cell--team-member',
            renderCell: (member) => renderTeamMemberText(member.phone)
          },
          {
            key: 'type',
            label: 'Member Type',
            mobileLabel: 'Member Type',
            width: 'minmax(0, 300px)',
            cellClassName: 'trade-data-list__cell--team-member',
            renderCell: (member) => renderTeamMemberText(getMemberTypeLabel(member))
          }
        ],
        rowClassName: (member) => member.isPending ? 'trade-data-list__row--pending' : '',
        rowAttributes: (member) => ({
          'data-team-member-id': getTeamMemberRowId(member)
        })
      };
    };

    const buildSalesRepSection = (salesRep = {}) => {
      const hasRep = Boolean(
        (salesRep.name && salesRep.name.trim() && !salesRep.name.includes('undefined'))
        || (salesRep.email && salesRep.email.trim())
      );

      if (!hasRep) {
        return window.cloneTemplate('tpl-company-sales-rep-unassigned');
      }

      const clone = window.cloneTemplate('tpl-company-sales-rep');
      clone.querySelector('[data-tpl-rep-name]').textContent = salesRep.name || '';
      clone.querySelector('[data-tpl-rep-email]').textContent = salesRep.email || '';
      return clone;
    };

    const buildCompanyDetailsContent = ({ companyData = {}, salesRepFragment = null, isAdmin = false } = {}) => {
      const address = companyData?.address || {};
      const address2 = typeof address.address2 === 'string' ? address.address2.trim() : '';

      const clone = window.cloneTemplate('tpl-company-details');

      clone.querySelector('[data-tpl-company-name]').textContent = getDetailValue(companyData?.name);
      clone.querySelector('[data-tpl-company-email]').textContent = getDetailValue(companyData?.email);
      clone.querySelector('[data-tpl-address1]').textContent = getDetailValue(address.address1);

      if (address2) {
        const addr2Section = clone.querySelector('[data-tpl-address2-section]');
        addr2Section.style.display = '';
        clone.querySelector('[data-tpl-address2]').textContent = address.address2;
      }

      clone.querySelector('[data-tpl-city]').textContent = getDetailValue(address.city);
      clone.querySelector('[data-tpl-state]').textContent = getDetailValue(address.state);
      clone.querySelector('[data-tpl-country]').textContent = getDetailValue(address.country);
      clone.querySelector('[data-tpl-zip]').textContent = getDetailValue(address.zipPostalCode);

      // Sales rep
      const repSlot = clone.querySelector('[data-tpl-sales-rep-slot]');
      if (salesRepFragment) {
        repSlot.replaceWith(salesRepFragment);
      }

      // Non-admin info
      if (!isAdmin) {
        clone.querySelector('[data-tpl-non-admin-info]').style.display = '';
      }

      return clone;
    };

    const renderCompanyDetails = async () => {
      // Show loading state while fetching company data
      showLoading();

      try {
        const response = await tradeFetch(`${window.tradeApiBase}/companies`, undefined, { source: 'TradeCompany.fetchCompany' });
        const data = await response.json();
        const companyData = data.company;
        isCurrentUserAdmin = data.isAdmin || false;

        const salesRepFragment = buildSalesRepSection(companyData?.salesRep || {});

        // Hide loading and show content
        hideLoading();
        if (companyContentView) companyContentView.classList.remove(hiddenClass);

        companyContentContainer.replaceChildren(buildCompanyDetailsContent({
          companyData,
          salesRepFragment,
          isAdmin: isCurrentUserAdmin
        }));

        // Store company data for modal use
        window.currentCompanyData = companyData;
        window.currentCompanyId = companyData?.id || null;

        // Update footer button text — only on success
        const footer = document.getElementById('company-footer');
        const footerButton = footer?.querySelector('button');
        if (footer) {
          footer.classList.toggle(hiddenClass, !isCurrentUserAdmin);
        }
        if (footerButton && isCurrentUserAdmin) {
          footerButton.textContent = 'UPDATE ACCOUNT DETAILS';
          footerButton.setAttribute('data-open-update-company', '');
          footerButton.removeAttribute('data-open-add-member');
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        if (window.trackTradeError) trackTradeError(error, { source: 'TradeCompany.renderCompanyDetails' });
        hideLoading();
        if (companyContentView) companyContentView.classList.remove(hiddenClass);

        companyContentContainer.innerHTML = renderTradeState({
          type: 'error',
          title: 'We were unable to load your company details',
          actionText: 'Try Again',
          onRetry: renderCompanyDetails,
        });

        const footer = document.getElementById('company-footer');
        if (footer) footer.classList.add(hiddenClass);
      }
    };

    const renderTeamMembers = async () => {
      // Show loading state
      showLoading();

      try {
        // Fetch team members from API
        const response = await tradeFetch(`${window.tradeApiBase}/users`, undefined, { source: 'TradeCompany.fetchTeamMembers' });
        const data = await response.json();

        const teamMembers = data.users;
        currentTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];

        // Hide loading and show content
        hideLoading();
        if (companyContentView) companyContentView.classList.remove(hiddenClass);
        companyContentContainer.innerHTML = '';

        if (teamMembers && teamMembers.length > 0) {
          companyContentContainer.innerHTML = window.renderTradeDataList(
            createTeamMemberListConfig(currentTeamMembers)
          );
        } else {
          companyContentContainer.innerHTML = renderTradeState({
              type: 'empty',
              title: 'No Team Members',
              message: 'Team members added to your account will appear here.',
            });
        }

        // Update footer button text — only on success
        const footer = document.getElementById('company-footer');
        const footerButton = footer?.querySelector('button');
        if (footer) {
          footer.classList.remove(hiddenClass);
        }
        if (footerButton) {
          footerButton.textContent = 'ADD A NEW TEAM MEMBER';
          footerButton.setAttribute('data-open-add-member', '');
          footerButton.removeAttribute('data-open-update-company');
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
        // Hide loading and show content area for error message
        hideLoading();
        if (companyContentView) companyContentView.classList.remove(hiddenClass);
        companyContentContainer.innerHTML = renderTradeState({
            type: 'error',
            title: 'We were unable to load your team members',
            actionText: 'Try Again',
            onRetry: renderTeamMembers,
          });

        const footer = document.getElementById('company-footer');
        if (footer) footer.classList.add(hiddenClass);
      }
    };

    const renderResaleCertificate = async () => {
      // Show loading state
      showLoading();

      try {
        const response = await tradeFetch(`${window.tradeApiBase}/companies/certificates`, undefined, { source: 'TradeCompany.fetchCertificates' });
        const data = await response.json();
        const certificates = data.certificates || [];

        // Hide loading and show content
        hideLoading();
        if (companyContentView) companyContentView.classList.remove(hiddenClass);

        if (certificates.length === 0) {
          // No certificates - show upload prompt
          companyContentContainer.replaceChildren(window.cloneTemplate('tpl-company-certificates-empty'));
        } else {
          // Show certificates list
          const listClone = window.cloneTemplate('tpl-company-certificates-list');
          const certList = listClone.querySelector('[data-tpl-cert-list]');

          certificates.forEach(function(cert) {
            const normalizedStatus = (cert.status || '').toLowerCase();
            const statusClass = normalizedStatus === 'approved'
              ? 'approved'
              : (normalizedStatus === 'rejected' || normalizedStatus === 'expired' ? normalizedStatus : 'pending');
            const statusLabel = normalizedStatus
              ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)
              : 'Pending';
            const expiryDate = cert.expirationDate ? new Date(cert.expirationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

            const certClone = window.cloneTemplate('tpl-company-certificate');
            certClone.querySelector('[data-tpl-cert-icon]').src = certificateFileIconUrl;
            certClone.querySelector('[data-tpl-cert-number]').textContent = cert.exemptionNumber;
            certClone.querySelector('[data-tpl-cert-region]').textContent = cert.region;
            certClone.querySelector('[data-tpl-cert-status-dot]').className = 'certificate-list-item__status-dot certificate-list-item__status-dot--' + statusClass;
            certClone.querySelector('[data-tpl-cert-status-label]').textContent = statusLabel;

            if (expiryDate) {
              const expiryEl = certClone.querySelector('[data-tpl-cert-expiry]');
              expiryEl.textContent = 'Expires ' + expiryDate;
              expiryEl.style.display = '';
            }

            certList.appendChild(certClone);
          });

          companyContentContainer.replaceChildren(listClone);
        }
      } catch (error) {
        console.error('Error fetching certificates:', error);
        if (window.trackTradeError) trackTradeError(error, { source: 'TradeCompany.renderCertificates' });
        hideLoading();
        if (companyContentView) companyContentView.classList.remove(hiddenClass);

        companyContentContainer.innerHTML = renderTradeState({
            type: 'error',
            title: 'We were unable to load your certificates',
            actionText: 'Try Again',
            onRetry: renderResaleCertificate,
          });
      }

      // Hide footer for resale certificate view
      const footer = document.getElementById('company-footer');
      if (footer) footer.classList.add(hiddenClass);
    };

    // Function to toggle company dropdown menu
    window.toggleCompanyMenu = () => {
      const menu = document.getElementById('company-dropdown-menu');
      if (!menu) return;

      // Toggle current menu
      menu.classList.toggle(hiddenClass);
    };

    // Close company dropdown menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.company-actions-mobile')) {
        const menu = document.getElementById('company-dropdown-menu');
        if (menu) {
          menu.classList.add(hiddenClass);
        }
      }
    });

    // Use event delegation on document for dynamically created elements
    document.addEventListener('click', (e) => {
      const target = e.target;

      // Handle navigation button clicks (desktop)
      if (target && target.closest('.company-actions')) {
        const button = target.closest('button');
        if (!button) return;

        const buttonText = button.textContent.trim();

        switch (buttonText) {
          case 'COMPANY DETAILS':
            section = '';
            break;
          case 'TEAM MEMBERS':
            section = 'team-members';
            break;
          case 'RESALE CERTIFICATE':
            section = 'resale-certificate';
            break;
        }

        // Render selected view without reloading
        toggleView();
        return;
      }

      // Handle mobile dropdown navigation
      if (target && target.classList && target.classList.contains('company-dropdown-item')) {
        const next = target.getAttribute('data-section') || '';
        section = next;
        // Close the dropdown
        const menu = document.getElementById('company-dropdown-menu');
        if (menu) menu.classList.add(hiddenClass);
        // Render selected view without reloading
        toggleView();
        return;
      }

      // Handle team member row click (View Member modal)
      const memberRow = target && target.closest ? target.closest('.trade-data-list--team-members .trade-data-list__row--clickable') : null;
      if (memberRow) {
        if (target.closest('a, button')) return;
        const memberId = memberRow.getAttribute('data-team-member-id');
        const selectedMember = currentTeamMembers.find((member) => getTeamMemberRowId(member) === memberId);
        if (selectedMember && window.TradeViewMemberModal) {
          TradeViewMemberModal.render(selectedMember);
          TradeViewMemberModal.open();
        }
        return;
      }
    });

    // Listen for custom events dispatched by modal JS files
    document.addEventListener('trade:member-invited', () => {
      if (section === 'team-members') renderTeamMembers();
    });
    document.addEventListener('trade:company-updated', () => {
      if (section === '' || !section) renderCompanyDetails();
    });
    document.addEventListener('trade:certificate-uploaded', () => {
      if (section === 'resale-certificate') renderResaleCertificate();
    });

    // Initialize the view on page load
    toggleView();
  });
})();
